import React, { useState, useEffect } from 'react';
import Routes from './routes/Routes';
import { AuthProvider } from './components/auth-context/AuthContext';
import { ProductType } from './components/product-type-context/ProductTypeContext';
import { useDispatch } from 'react-redux';
import { setProduct } from './features/productType/productTypeSlice';
import {
    intialize,
    trackEvent,
    setupOfflineAnalytics,
    setupOnlineListener,
} from './segment';

const App: React.FC = () => {
    const dispatch = useDispatch();
    const [productType, setProductType] = useState<ProductType | null>(null);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [analyticsInitialized, setAnalyticsInitialized] = useState(false);

    // Event listener for online status change
    const handleOnline = () => {
        setIsOnline(true);
        // Only track this event if analytics is already initialized
        if (analyticsInitialized) {
            try {
                //old event
                trackEvent('internet_connected', {});
            } catch (error) {
                console.warn('Failed to track online event:', error);
            }
        }
    };

    // Event listener for offline status change
    const handleOffline = () => {
        setIsOnline(false);
    };

    // Initialize analytics based on connectivity
    useEffect(() => {
        try {
            if (isOnline) {
                intialize();
            } else {
                setupOfflineAnalytics();
            }
            setupOnlineListener();
            setAnalyticsInitialized(true);
        } catch (error) {
            console.warn('Analytics initialization error:', error);
            // Create fallback to prevent further errors
            setupOfflineAnalytics();
            setAnalyticsInitialized(true);
        }
    }, []);

    // Add network status event listeners
    useEffect(() => {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [analyticsInitialized]);

    // Determine product type
    useEffect(() => {
        const determineProductType = async () => {
            const hostname = window.location.hostname;
            if (
                hostname.includes('mybuildr.ai') ||
                process.env.REACT_APP_APPLICATION_NAME === 'Buildr'
            ) {
                setProductType('Buildr' as ProductType);
            } else {
                try {
                    // TODO: Replace with actual API call
                    setProductType('ActiveTeach' as ProductType);
                } catch (error) {
                    console.error('Error fetching product type:', error);
                    setProductType('ActiveTeach' as ProductType);
                }
            }
        };
        determineProductType();
    }, []);

    // Set favicon based on product type
    useEffect(() => {
        if (productType) {
            let typeOfProduct;
            const addFavicon = () => {
                const link = document.createElement('link');
                const title = document.createElement('title');
                link.rel = 'icon';

                if (productType === 'Buildr') {
                    typeOfProduct = 'Buildr';
                    link.href = `/favicon_buildr.png`;
                    title.textContent = 'Buildr';
                } else {
                    typeOfProduct = 'Active Teach';
                    link.href = `/SmartTeach.ico`;
                    title.textContent = 'Smart Teach';
                }

                // Only track if online and analytics is initialized
                if (isOnline && analyticsInitialized) {
                    try {
                        //old event
                        trackEvent('product_type', {
                            product_type: typeOfProduct,
                        });
                    } catch (error) {
                        console.warn('Failed to track product type:', error);
                    }
                }

                document.head.appendChild(link);
                if (process.env.REACT_APP_INSTALLATION_MODE === 'webapp') {
                    document.head.appendChild(title);
                    localStorage.setItem(
                        'isWebApp',
                        (
                            process.env.REACT_APP_INSTALLATION_MODE === 'webapp'
                        ).toString()
                    );
                }
            };

            addFavicon();
        }
    }, [productType, isOnline, analyticsInitialized]);

    // Update Redux store
    useEffect(() => {
        if (productType) {
            dispatch(setProduct(productType));
        }
    }, [productType, dispatch]);

    if (productType === null) {
        return <div>Loading...</div>;
    }

    return (
        <AuthProvider>
            <Routes />
        </AuthProvider>
    );
};

export default App;
